import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Spinner, Form } from 'react-bootstrap';
import axios from 'axios';
import * as constants from '../../constants/CONSTANT';

/** Same idea as backend FIELD_PERM_AUDIT_EXCLUDE_LOWER — not shown in FLS UI. */
const FIELD_PERM_AUDIT_EXCLUDE_LOWER = new Set(
  ['id', 'createddate', 'lastmodifieddate', 'createdbyid', 'lastmodifiedbyid', 'ownerid', 'is_deleted'].map((s) =>
    s.toLowerCase()
  )
);

function parseObjectConfigBlock(raw) {
  if (!raw || typeof raw !== 'object') return {};
  let c = raw.config;
  if (typeof c === 'string') {
    try {
      c = JSON.parse(c);
    } catch {
      c = {};
    }
  }
  if (!c || typeof c !== 'object') c = {};
  return c;
}

function buildFieldPermExcludedLowerSet(configBlock) {
  const set = new Set(FIELD_PERM_AUDIT_EXCLUDE_LOWER);
  const hf = Array.isArray(configBlock.hidden_fields) ? configBlock.hidden_fields : [];
  for (const n of hf) {
    if (n != null && String(n).trim() !== '') {
      set.add(String(n).trim().toLowerCase());
    }
  }
  return set;
}

function filterFieldsForFieldPermissions(fields, excludedLower) {
  return (Array.isArray(fields) ? fields : []).filter((f) => {
    const fn = f.field_name != null ? String(f.field_name).trim() : '';
    if (!fn) return false;
    return !excludedLower.has(fn.toLowerCase());
  });
}

/** Object-level required (or layout-required on merged field row): cannot deny Edit while Read is on. */
function fieldIsRequiredForRoleFieldPermissions(f) {
  if (!f || typeof f !== 'object') return false;
  return f.is_required === true || f.is_required_layout === true;
}

/**
 * Field-level security editor for a role + object.
 * Persists only denials: role.permission_config.fieldPermissionsByObject stores sparse
 * fieldApiName → { can_read: false } or { can_edit: false }; omitted fields = object defaults.
 */
const FieldPermissionsModal = ({
  show,
  onHide,
  permission,
  /** Sparse denials from API; UI merges with object Read/Update defaults */
  overridesForObject = {},
  /** When true, checkboxes disabled and Save hidden */
  readOnly = false,
  /** (objectName, sparseDenials) => void — only fields denied vs defaults */
  onApply,
}) => {
  const objectName = permission?.object_name;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fields, setFields] = useState([]);
  const [rowState, setRowState] = useState({});
  const [fieldSearch, setFieldSearch] = useState('');
  const readHeaderRef = useRef(null);
  const editHeaderRef = useRef(null);

  const defaultRead = permission?.can_read === true;
  const defaultEdit = defaultRead && permission?.can_update === true;

  const overridesSerialized = useMemo(
    () => JSON.stringify(overridesForObject && typeof overridesForObject === 'object' ? overridesForObject : {}),
    [overridesForObject]
  );

  const sortedFields = useMemo(() => {
    const list = Array.isArray(fields) ? [...fields] : [];
    list.sort((a, b) => {
      const ao = a.display_order != null ? Number(a.display_order) : 0;
      const bo = b.display_order != null ? Number(b.display_order) : 0;
      if (ao !== bo) return ao - bo;
      const la = String(a.field_label || a.field_name || '');
      const lb = String(b.field_label || b.field_name || '');
      return la.localeCompare(lb);
    });
    return list;
  }, [fields]);

  const filteredFields = useMemo(() => {
    const q = fieldSearch.trim().toLowerCase();
    if (!q) return sortedFields;
    return sortedFields.filter((f) => {
      const fn = String(f.field_name || '').toLowerCase();
      const fl = String(f.field_label || '').toLowerCase();
      return fn.includes(q) || fl.includes(q);
    });
  }, [sortedFields, fieldSearch]);

  const filteredFieldNames = useMemo(
    () => filteredFields.map((f) => String(f.field_name || '').trim()).filter(Boolean),
    [filteredFields]
  );

  const { allFilteredRead, someFilteredRead, allFilteredEdit, someFilteredEdit } = useMemo(() => {
    const n = filteredFieldNames.length;
    if (n === 0) {
      return {
        allFilteredRead: false,
        someFilteredRead: false,
        allFilteredEdit: false,
        someFilteredEdit: false,
      };
    }
    let readOn = 0;
    let editOn = 0;
    for (const fn of filteredFieldNames) {
      const st = rowState[fn] || { can_read: defaultRead, can_edit: defaultEdit };
      if (st.can_read !== false) readOn += 1;
      if (st.can_edit === true) editOn += 1;
    }
    return {
      allFilteredRead: readOn === n,
      someFilteredRead: readOn > 0 && readOn < n,
      allFilteredEdit: editOn === n,
      someFilteredEdit: editOn > 0 && editOn < n,
    };
  }, [filteredFieldNames, rowState, defaultRead, defaultEdit]);

  useEffect(() => {
    const el = readHeaderRef.current;
    if (el) {
      el.indeterminate = filteredFieldNames.length > 0 && someFilteredRead;
    }
  }, [filteredFieldNames.length, someFilteredRead, allFilteredRead]);

  useEffect(() => {
    const el = editHeaderRef.current;
    if (el) {
      el.indeterminate = filteredFieldNames.length > 0 && someFilteredEdit;
    }
  }, [filteredFieldNames.length, someFilteredEdit, allFilteredEdit]);

  useEffect(() => {
    if (!show || !objectName) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      setFields([]);
      setRowState({});
      setFieldSearch('');
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${constants.API_BASE_URL}/api/objectconfig/${encodeURIComponent(objectName)}`,
          { headers: { Authorization: token } }
        );
        if (!res.data?.success || !res.data.data) {
          throw new Error(res.data?.message || 'Failed to load object');
        }
        const payload = res.data.data;
        const configBlock = parseObjectConfigBlock(payload);
        const excludedLower = buildFieldPermExcludedLowerSet(configBlock);
        const list = filterFieldsForFieldPermissions(payload.fields, excludedLower);
        setFields(list);

        const next = {};
        const dr = permission?.can_read === true;
        const de = dr && permission?.can_update === true;
        let parsedOverrides = {};
        try {
          parsedOverrides = JSON.parse(overridesSerialized) || {};
        } catch {
          parsedOverrides = {};
        }
        for (const f of list) {
          const fn = f.field_name != null ? String(f.field_name) : '';
          if (!fn) continue;
          let can_read = dr;
          let can_edit = de;
          const o = parsedOverrides[fn];
          if (o && typeof o === 'object') {
            if (Object.prototype.hasOwnProperty.call(o, 'can_read')) {
              can_read = o.can_read !== false;
            }
            if (Object.prototype.hasOwnProperty.call(o, 'can_edit')) {
              can_edit = o.can_edit === true;
            }
            // Sparse { can_read: false } must not be overwritten by object-default can_edit === true
            if (!can_read) {
              can_edit = false;
            } else if (can_edit) {
              can_read = true;
            }
            if (!dr) {
              can_read = false;
              can_edit = false;
            }
          }
          if (fieldIsRequiredForRoleFieldPermissions(f) && can_read && de) {
            can_edit = true;
          }
          next[fn] = { can_read, can_edit };
        }
        setRowState(next);
      } catch (e) {
        setError(e.response?.data?.message || e.message || 'Failed to load fields');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [show, objectName, permission?.can_read, permission?.can_update, overridesSerialized]);

  /** Sparse map: only fields where Read or Edit is denied vs object-level defaults. */
  const buildSparseFieldPermissionDenials = () => {
    const out = {};
    const dr = permission?.can_read === true;
    const de = dr && permission?.can_update === true;
    if (!dr) return out;
    for (const fn of Object.keys(rowState)) {
      const v = rowState[fn];
      if (!v) continue;
      let can_read = v.can_read !== false;
      let can_edit = v.can_edit === true;
      if (can_edit) can_read = true;
      const fieldRow = fields.find((x) => String(x.field_name) === fn);
      if (!can_read) {
        out[fn] = { can_read: false };
      } else if (!can_edit && de) {
        if (fieldIsRequiredForRoleFieldPermissions(fieldRow)) continue;
        out[fn] = { can_edit: false };
      }
    }
    return out;
  };

  const handleSave = () => {
    if (readOnly || !onApply) {
      onHide();
      return;
    }
    onApply(objectName, buildSparseFieldPermissionDenials());
    onHide();
  };

  const setCell = (fieldName, key, value) => {
    if (readOnly) return;
    const fieldRow = fields.find((x) => String(x.field_name) === String(fieldName));
    if (key === 'can_edit' && value === false && fieldIsRequiredForRoleFieldPermissions(fieldRow)) {
      return;
    }
    setRowState((prev) => {
      const cur = prev[fieldName] || { can_read: defaultRead, can_edit: defaultEdit };
      let can_read = cur.can_read;
      let can_edit = cur.can_edit;
      if (key === 'can_read') {
        can_read = value;
        if (!can_read) can_edit = false;
        else if (fieldIsRequiredForRoleFieldPermissions(fieldRow) && defaultEdit) can_edit = true;
      } else {
        can_edit = value;
        if (can_edit) can_read = true;
      }
      return { ...prev, [fieldName]: { can_read, can_edit } };
    });
  };

  const setBulkReadForFiltered = (value) => {
    if (readOnly || !defaultRead) return;
    setRowState((prev) => {
      const next = { ...prev };
      for (const f of filteredFields) {
        const fn = f.field_name != null ? String(f.field_name).trim() : '';
        if (!fn) continue;
        const cur = next[fn] || { can_read: defaultRead, can_edit: defaultEdit };
        let can_read = value;
        let can_edit = cur.can_edit;
        if (!can_read) can_edit = false;
        else if (fieldIsRequiredForRoleFieldPermissions(f) && defaultEdit) can_edit = true;
        next[fn] = { can_read, can_edit };
      }
      return next;
    });
  };

  const setBulkEditForFiltered = (value) => {
    if (readOnly || !defaultRead || !permission?.can_update) return;
    setRowState((prev) => {
      const next = { ...prev };
      for (const f of filteredFields) {
        const fn = f.field_name != null ? String(f.field_name).trim() : '';
        if (!fn) continue;
        const cur = next[fn] || { can_read: defaultRead, can_edit: defaultEdit };
        let can_edit = value;
        let can_read = cur.can_read;
        if (!can_edit && fieldIsRequiredForRoleFieldPermissions(f) && cur.can_read !== false) {
          can_edit = true;
        } else if (can_edit) {
          can_read = true;
        }
        next[fn] = { can_read, can_edit };
      }
      return next;
    });
  };

  const objectLabel = permission?.object_display_name || objectName || '';

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Field Permissions — {objectLabel}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!defaultRead && (
          <div className="alert alert-warning py-2 small mb-3">
            Object <strong>Read</strong> is off for this role. Field access is hidden until Read is granted at the object level.
          </div>
        )}
        {defaultRead && permission?.can_update !== true && (
          <div className="alert alert-secondary py-2 small mb-3">
            Object <strong>Update</strong> is off for this role. The <strong>Edit</strong> column shows saved field settings but cannot be changed until Update is granted at the object level.
          </div>
        )}
        {error && <div className="alert alert-danger py-2 small">{error}</div>}
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading fields…
          </div>
        )}
        {!loading && !error && sortedFields.length === 0 && (
          <p className="text-muted small mb-0">
            No configurable fields for this object. System and currency plumbing fields (for example id, exchange_rate,
            base_price) always follow object-level permissions.
          </p>
        )}
        {!loading && !error && sortedFields.length > 0 && (
          <>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3 field-permissions-modal-toolbar">
              <div className="flex-grow-1 position-relative" style={{ minWidth: '220px', maxWidth: '420px' }}>
                <i
                  className="fas fa-search position-absolute text-muted"
                  style={{ left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: 14 }}
                  aria-hidden="true"
                />
                <Form.Control
                  type="search"
                  className="ps-4"
                  placeholder="Search fields by label or API name…"
                  value={fieldSearch}
                  onChange={(e) => setFieldSearch(e.target.value)}
                  disabled={readOnly}
                  autoComplete="off"
                />
              </div>
              {fieldSearch.trim() ? (
                <span className="text-muted small">
                  {filteredFields.length === sortedFields.length
                    ? `${sortedFields.length} fields`
                    : `${filteredFields.length} of ${sortedFields.length} fields`}
                </span>
              ) : null}
            </div>
            {filteredFields.length === 0 ? (
              <p className="text-muted small mb-0">No fields match your search.</p>
            ) : (
          <div className="table-responsive field-permissions-modal-table">
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Field</th>
                  <th className="text-center" style={{ width: '100px' }}>
                    <div className="d-flex flex-column align-items-center gap-1">
                      <span>Read</span>
                      <input
                        ref={readHeaderRef}
                        type="checkbox"
                        className="form-check-input"
                        checked={allFilteredRead}
                        disabled={readOnly || !defaultRead}
                        onChange={(e) => setBulkReadForFiltered(e.target.checked)}
                        title="Select all Read (filtered rows)"
                        aria-label="Select all Read for filtered fields"
                      />
                    </div>
                  </th>
                  <th className="text-center" style={{ width: '100px' }}>
                    <div className="d-flex flex-column align-items-center gap-1">
                      <span>Edit</span>
                      <input
                        ref={editHeaderRef}
                        type="checkbox"
                        className="form-check-input"
                        checked={allFilteredEdit}
                        disabled={readOnly || !defaultRead || !permission?.can_update}
                        onChange={(e) => setBulkEditForFiltered(e.target.checked)}
                        title={
                          !permission?.can_update
                            ? 'Grant object Update to change Edit permissions'
                            : 'Select all Edit (filtered rows)'
                        }
                        aria-label="Select all Edit for filtered fields"
                      />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredFields.map((f) => {
                  const fn = f.field_name != null ? String(f.field_name) : '';
                  if (!fn) return null;
                  const st = rowState[fn] || { can_read: defaultRead, can_edit: defaultEdit };
                  const readDisabled = readOnly || !defaultRead;
                  const requiredLocksEdit =
                    fieldIsRequiredForRoleFieldPermissions(f) && st.can_read !== false && defaultEdit;
                  const editDisabled =
                    readOnly || !defaultRead || !permission?.can_update || requiredLocksEdit;
                  return (
                    <tr key={fn}>
                      <td>
                        <div className="fw-medium">{f.field_label || fn}</div>
                        <div className="text-muted small">{fn}</div>
                        {requiredLocksEdit ? (
                          <div className="text-muted small mt-1">Required — Edit stays on with Read.</div>
                        ) : null}
                      </td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={st.can_read !== false}
                          disabled={readDisabled}
                          onChange={(e) => setCell(fn, 'can_read', e.target.checked)}
                          title={!defaultRead ? 'Enable object Read first' : ''}
                        />
                      </td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={st.can_edit === true}
                          disabled={editDisabled}
                          onChange={(e) => setCell(fn, 'can_edit', e.target.checked)}
                          title={
                            requiredLocksEdit
                              ? 'Required fields must keep Edit when Read is granted'
                              : !permission?.can_update
                                ? 'Object Update is off'
                                : ''
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
        {!readOnly && (
          <Button variant="primary" onClick={handleSave} disabled={loading || !!error}>
            Save
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default FieldPermissionsModal;
